import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Form, FormGroup, Image, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Loading from '../Loading';
import { getInvoicePaymentDetailsService, printInvoiceService } from '../../services/invoicesService';
import { createTransactionService } from '../../services/transactionsService';
import {
    applyPortalTextColor,
    convertQueryStringToObject,
    errorSwal,
    formatCurrency,
    mobileAndTabletCheck,
    normalizePaymentError,
    roundTo,
} from '../../utils/helper';
import Swal from 'sweetalert2';
import './index.scss';
import { makeBookingPortalPaymentService } from '../../services/paymentService';
import PayrocCreditCardPaymentForm from '../PayrocCreditCardPaymentForm';
import CustomerInfoFormSection from '../PaymentFormSections/customerInfoFormSection';
import FiservCardNumberField from '../FiservCardNumberField';
import { useFeatureToggle, FEATURE_TOGGLE_IDS } from '../../utils/hooks/useFeatureToggle';

const InvoicePayment = () => {
    const queryObj = convertQueryStringToObject(window.location.search);
    const ts = queryObj.ts;
    const invoiceId = queryObj.invoiceId;

    const {
        register,
        handleSubmit,
        trigger,
        formState: { errors },
    } = useForm({
        defaultValues: { country: 'US' },
    });
    const [loading, setLoading] = useState(true);
    const [companyDetails, setCompanyDetails] = useState({});
    const [tripSetting, setTripSetting] = useState({});
    const [invoice, setInvoice] = useState({});
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [invoiceTotal, setInvoiceTotal] = useState();
    const [invoiceAmount, setInvoiceAmount] = useState();
    const [surcharge, setSurcharge] = useState({
        amount: null,
        percentage: null,
    });
    const [basePaymentAmount, setBasePaymentAmount] = useState();
    const [totalPaymentAmount, setTotalPaymentAmount] = useState();
    const [amountOutstanding, setAmountOutstanding] = useState();
    const [isOverPayment, setIsOverPayment] = useState(false);
    const [displayAmountOutstanding, setDisplayAmountOutstanding] = useState();
    const [disablePayment, setDisablePayment] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [formState, setFormState] = useState({
        stage: 0,
        transactionId: '',
        amount: '',
        surchargeRemoved: false,
    });
    const [activeSection, setActiveSection] = useState('customerInfo');
    const [creditCardSessionTimer, setCreditCardSessionTimer] = useState(null);
    const basePaymentAmountRef = useRef(basePaymentAmount);
    const totalPaymentAmountRef = useRef(totalPaymentAmount);
    const surchargeAmountRef = useRef(surcharge?.amount);
    const [fiservToken, setFiservToken] = useState('');
    const [fiservError, setFiservError] = useState(null);
    const [fiservFieldKey, setFiservFieldKey] = useState(0);
    const fiservFieldRef = useRef(null);

    const isFiservHostedFieldsEnabled = useFeatureToggle(FEATURE_TOGGLE_IDS.FISERV_HOSTED_FIELDS);
    const fiservHostedFieldsActive = companyDetails?.paymentProcessorTypeID === 3 && isFiservHostedFieldsEnabled;

    // A charge consumes the token and the expiration/CVV bound to it, so any retry needs freshly entered card details
    const resetFiservCard = () => {
        setFiservToken('');
        setFiservError(null);
        setFiservFieldKey((key) => key + 1);
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                let allData = await getInvoicePaymentDetailsService(invoiceId, ts);
                if (allData && allData.data) {
                    allData = allData.data;
                    setInvoice(allData.invoice);

                    //remainingAmount
                    let invoicedAmount = Number(allData.invoice.invoicedAmount);
                    let amountOutstanding = allData.invoice.amountOutstanding;
                    let surchargePercentage = allData.invoice.surchargePercentage;
                    let surchargeAmount = amountOutstanding * (surchargePercentage / 100);

                    setSurcharge({
                        amount: formatCurrency(surchargeAmount),
                        percentage: surchargePercentage,
                    });
                    setInvoiceTotal(invoicedAmount);

                    setBasePaymentAmount(amountOutstanding);
                    setTotalPaymentAmount(roundTo(Number(amountOutstanding) + Number(surchargeAmount), 2));

                    if (allData.invoice.disableOnlinePayment) setDisablePayment(true);
                    setAmountOutstanding(amountOutstanding > 0 ? amountOutstanding : '');
                    setDisplayAmountOutstanding(amountOutstanding > 0 ? amountOutstanding : '');
                    applyPortalTextColor(allData.accountSettings?.portalTextColor);
                    setTripSetting(allData.accountSettings);
                    setCompanyDetails(allData.company);
                    setStates(allData.states);
                    setCountries(allData.countries);
                } else {
                    setLoadError(true);
                }
            } catch (error) {
                console.log(error);
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [invoiceId, ts]);

    useEffect(() => {
        if (!creditCardSessionTimer) return;

        const interval = setInterval(() => {
            setCreditCardSessionTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [creditCardSessionTimer]);

    useEffect(() => {
        basePaymentAmountRef.current = basePaymentAmount;
        totalPaymentAmountRef.current = totalPaymentAmount;
        surchargeAmountRef.current = surcharge?.amount;
    }, [basePaymentAmount, totalPaymentAmount, surcharge]);

    const handleViewInvoice = async () => {
        try {
            setLoading(true);
            const pdfData = await printInvoiceService(invoiceId, companyDetails.id);
            setLoading(false);
            let blob = new Blob([pdfData.data], { type: 'application/pdf' });
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);

            if (mobileAndTabletCheck()) {
                window.open(link.href);
            } else {
                link.download = `Invoice ${invoice.invoiceNumber}.pdf`;
                link.click();
            }
        } catch (e) {
            setLoading(false);
            console.log(e);
        }
    };

    const handleSubmitPayment = async (e, token = null) => {
        if (e) e.preventDefault();
        await handleSubmit(async (data) => {
            if (Number(basePaymentAmount) > Number(amountOutstanding)) {
                Swal.fire({
                    title: 'Overpayment',
                    text: 'You are attempting to pay more than what is owed. Please adjust your payment amount accordingly before submitting payment.',
                    icon: 'warning',
                });
                setIsOverPayment(true);
                return;
            }
            setIsOverPayment(false);
            let chargeAttempted = false;
            try {
                setLoading(true);
                const fiservCard = fiservHostedFieldsActive ? await fiservFieldRef.current.getToken() : null;
                if (fiservCard && !fiservCard.token) {
                    setLoading(false);
                    return Swal.fire({ title: 'Card Details Required', text: fiservCard.message, icon: 'error' });
                }
                if (data && data.cardNumber && data.cardNumber.length < 15) {
                    setLoading(false);
                    return Swal.fire({
                        title: 'Card Number Error',
                        text: 'Please enter a valid Card Number',
                        icon: 'error',
                    });
                }
                let user = JSON.parse(localStorage.getItem('user'));
                let transactionData = {
                    ...data,
                    bookingID: invoice.bookingID ? invoice.bookingID : null,
                    invoiceID: invoiceId,
                    transactionTypeID: 3,
                    amount: Number(totalPaymentAmountRef?.current),
                    surchargeAmount: surchargeAmountRef?.current,
                    surchargeRate: surcharge.percentage,
                    userID: user?.id || null,
                    date: moment().format('YYYY-MM-DD'),
                };

                if (Number(invoiceAmount) > Number(invoiceTotal)) {
                    setLoading(false);
                    Swal.fire({
                        title: 'Invalid Payment Amount',
                        text: 'Invoice Payment amount can not be greater than Invoice Total',
                        icon: 'error',
                    });
                } else {
                    let transactionRecords = await createTransactionService(transactionData, companyDetails.id);
                    const {
                        transaction,
                        invoiceFreeEntryRecord = null,
                        surchargeTransaction = null,
                        addonAssignmentRecord = null,
                    } = transactionRecords.data;

                    let paymentResponse = null;
                    if (
                        Number(transactionData.transactionTypeID) === 3 ||
                        Number(transactionData.transactionTypeID) === 4
                    ) {
                        const paymentData = {
                            ...transactionData,
                            basePaymentAmount: basePaymentAmountRef?.current,
                            orderID: transaction.id,
                            companyID: companyDetails.id,
                        };
                        if (token) paymentData.token = token;
                        // The tokenizer returns a CardSecure token in place of the PAN; Fiserv takes it in the same field
                        if (fiservCard) paymentData.cardNumber = fiservCard.token;

                        if (invoiceFreeEntryRecord) paymentData.invoiceFreeEntryRecordID = invoiceFreeEntryRecord.id;
                        if (surchargeTransaction) paymentData.surchargeTransactionID = surchargeTransaction.id;
                        if (addonAssignmentRecord) paymentData.addonAssignmentRecordID = addonAssignmentRecord.id;

                        chargeAttempted = true;
                        const response = await makeBookingPortalPaymentService(paymentData, companyDetails.id);
                        paymentResponse = response.data;
                        if (typeof paymentResponse == 'undefined') {
                            Swal.fire({
                                title: 'Transaction Failed',
                                text: "We're Sorry. We have experienced an unknown error. Please wait a few minutes and try again. If the issue persists, please get in touch with us.",
                                icon: 'error',
                            });
                            setLoading(false);
                            return;
                        } else if (paymentResponse.errors && paymentResponse.errors.length > 0) {
                            errorSwal({
                                title: 'Transaction Declined',
                                text:
                                    paymentResponse.errors[0]?.msg ||
                                    paymentResponse.errors[0]?.message ||
                                    'There was an error with the response body. Please check all information and try again.', // msg = fiserv, message = payroc
                            });
                            setLoading(false);
                            return;
                        } else if (paymentResponse.error) {
                            Swal.fire({ title: 'Transaction Declined', text: paymentResponse.error, icon: 'error' });
                            setLoading(false);
                            return;
                        } else if (paymentResponse.Result === 'Declined') {
                            Swal.fire({ title: 'Transaction Declined', text: paymentResponse.Status, icon: 'error' });
                            setLoading(false);
                            return;
                        }
                    }
                    // The card has been charged by now, so a failed refresh must not turn this into "Payment Failed"
                    try {
                        let allData = await getInvoicePaymentDetailsService(invoiceId, ts);
                        if (allData && allData.data) {
                            allData = allData.data;
                            setInvoice(allData.invoice);
                            setInvoiceTotal(allData.invoice.invoicedAmount);

                            let amountOutstanding = roundTo(parseFloat(allData.invoice.amountOutstanding), 2);
                            setAmountOutstanding(amountOutstanding > 0 ? amountOutstanding : '');
                            setDisplayAmountOutstanding(amountOutstanding > 0 ? amountOutstanding : '');
                            setLoading(false);
                        }
                    } catch (refreshError) {
                        console.log(refreshError);
                    }
                    if (paymentResponse?.code === 'A') {
                        setFormState({
                            ...formState,
                            stage: 1,
                            transactionId: transaction.id,
                            amount: paymentResponse?.amountCharged,
                            surchargeRemoved: paymentResponse?.surchargeRemoved,
                        });
                    }
                    if (paymentResponse?.emailSent) {
                        Swal.fire({ title: 'Mail Sent', text: 'Email sent successfully', icon: 'success' });
                    }
                }
                setLoading(false);
            } catch (e) {
                if (e.response?.data?.errors?.length) {
                    const normalized = normalizePaymentError(e);
                    await errorSwal({
                        title: 'Payment Failed',
                        text:
                            normalized.message || 'An error occurred while processing your payment. Please try again.',
                    });
                    // // Only reload on Payroc to refresh hosted fields session
                    if (companyDetails?.paymentProcessorTypeID === 5) {
                        window.location.reload();
                    }
                } else {
                    errorSwal({
                        title: 'Payment Failed',
                        text: 'An unexpected error occurred while processing your payment. Please try again.',
                    });
                }
            } finally {
                if (chargeAttempted && fiservHostedFieldsActive) resetFiservCard();
                setLoading(false);
            }
        })();
    };

    const DisablePaymentModal = () => {
        return (
            <div className="model-box">
                <div>
                    <div className="confirm-logo mt-2">
                        {tripSetting && tripSetting.companyLogoUrl ? (
                            <Image src={tripSetting.companyLogoUrl} className="m-0 p-0 img-fluid" />
                        ) : (
                            ''
                        )}
                    </div>
                </div>

                <div className="pricelist-heading">
                    <div className="d-flex justify-content-center align-items-center flex-column">
                        <h5>Online Payments Disabled</h5>
                        <h6 className="disable-title">{`Online payments have been disabled for this Invoice. Please contact ${
                            companyDetails.displayName
                        } at ${
                            companyDetails.companyPhone ? companyDetails.companyPhone : '123456789'
                        } in order to arrange payment.`}</h6>
                    </div>
                    <div className=" d-flex align-items-center justify-content-center my-2">
                        <Image
                            src={
                                'https://storage.googleapis.com/the-bus-network-bucket/default/Powered-By-TBN-Logo.png'
                            }
                            className="m-0 p-0"
                        />
                    </div>
                </div>
            </div>
        );
    };

    const loadErrorMessage = (
        <div className="model-box">
            <div className="pricelist-heading">
                <div className="d-flex justify-content-center align-items-center flex-column">
                    <h5>Invoice Unavailable</h5>
                    <h6 className="disable-title">
                        This invoice could not be loaded. Please refresh the page or try again later.
                    </h6>
                </div>
            </div>
        </div>
    );

    const confirmation = () => {
        return (
            <div className="model-box">
                <div>
                    <div className="confirm-logo mt-2">
                        {tripSetting && tripSetting.companyLogoUrl ? (
                            <Image src={tripSetting.companyLogoUrl} className="m-0 p-0 img-fluid" />
                        ) : (
                            ''
                        )}
                    </div>
                </div>

                <div className="pricelist-heading">
                    <div className="d-flex justify-content-center align-items-center flex-column">
                        <h4>{`You Paid $${formatCurrency(formState.amount)}`}</h4>
                        <h6 className="title-price">{`${companyDetails.displayName} for Invoice ${invoice.invoiceNumber}`}</h6>
                    </div>
                    {formState?.surchargeRemoved && (
                        <div>
                            <p className="mb-0 mt-3">
                                Since the payment was made with a debit card, no surcharge was processed.
                            </p>
                        </div>
                    )}

                    <div className="payment-method d-flex align-items-center justify-content-between my-3">
                        <span className="title-text">Payment Method</span>
                        <span className="number">{`${formState?.surchargeRemoved ? 'Debit Card' : 'Credit Card'}`}</span>
                    </div>
                    <div className="payment-method d-flex align-items-center justify-content-between my-3">
                        <span className="title-text">Transaction ID</span>
                        <span className="number">{`${formState.transactionId}`}</span>
                    </div>
                    <div className="border-line"></div>
                    <div className="payment-method d-flex align-items-center justify-content-between my-3">
                        <span className="title-text">Due Date</span>
                        <span className="number">{`${
                            invoice.invoiceDueDate ? moment(invoice.invoiceDueDate).format('ll') : ''
                        }`}</span>
                    </div>
                    <div className="payment-method d-flex align-items-center justify-content-between my-3">
                        <span className="title-text">Invoice Total</span>
                        <span className="number">{`$${formatCurrency(invoiceTotal)}`}</span>
                    </div>
                    <div className="payment-method d-flex align-items-center justify-content-between my-3">
                        <span className="title-text">Remaining Balance</span>
                        <span className="number">{`$${formatCurrency(displayAmountOutstanding)}`}</span>
                    </div>

                    <div className=" d-flex align-items-center justify-content-center my-2">
                        <Image
                            src={
                                'https://storage.googleapis.com/the-bus-network-bucket/default/Powered-By-TBN-Logo.png'
                            }
                            className="m-0 p-0"
                        />
                    </div>
                </div>
            </div>
        );
    };

    const toggleStyle = {
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '1.2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        width: '100%',
        border: '0px',
        borderTop: '1px solid #dee2e6',
        borderBottom: '1px solid #dee2e6',
        color: '#4a4a4a',
        transition: 'all 0.2s ease',
        position: 'relative',
        backgroundColor: '#ffffff',
        marginBottom: '10px',
    };

    const arrowStyle = (isOpen) => ({
        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 0.3s ease',
        color: isOpen ? '#2c2c2c' : '#757575',
    });

    const handleGoToPaymentInformation = async () => {
        const isValid = await trigger();
        if (isValid) {
            setActiveSection('payment');
        }
    };

    const payrocPaymentForm = (
        <div className="my-3 w-100">
            <div
                onClick={() => setActiveSection('customerInfo')}
                style={{
                    ...toggleStyle,
                    backgroundColor: activeSection === 'customerInfo' ? '#f8f9fa' : '#ffffff',
                    cursor: 'pointer',
                }}
            >
                <span
                    style={{
                        fontWeight: activeSection === 'customerInfo' ? 700 : 600,
                        color: activeSection === 'customerInfo' ? '#2c2c2c' : '#4a4a4a',
                        letterSpacing: activeSection === 'customerInfo' ? '0.2px' : 'normal',
                    }}
                >
                    Contact Information
                </span>
                <i className="fas fa-chevron-down" style={arrowStyle(activeSection === 'customerInfo')} />
            </div>
            <div
                style={{
                    transition: 'max-height 0.3s ease',
                    overflow: 'hidden',
                    marginTop: '0px',
                    maxHeight: activeSection === 'customerInfo' ? '2000px' : '0',
                    visibility: activeSection === 'customerInfo' ? 'visible' : 'hidden',
                }}
            >
                <Row>
                    <CustomerInfoFormSection
                        states={states}
                        countries={countries}
                        register={register}
                        errors={errors}
                        includeName={false}
                    />
                </Row>
            </div>

            <div
                onClick={handleGoToPaymentInformation}
                style={{
                    ...toggleStyle,
                    backgroundColor: activeSection === 'payment' ? '#f8f9fa' : '#ffffff',
                    cursor: 'pointer',
                }}
            >
                <span
                    style={{
                        fontWeight: activeSection === 'payment' ? 700 : 600,
                        color: activeSection === 'payment' ? '#2c2c2c' : '#4a4a4a',
                        letterSpacing: activeSection === 'payment' ? '0.2px' : 'normal',
                    }}
                >
                    Payment Information
                </span>
                <i className="fas fa-chevron-down" style={arrowStyle(activeSection === 'payment')} />
            </div>

            <div
                style={{
                    transition: 'max-height 0.3s ease',
                    overflow: 'hidden',
                    maxHeight: activeSection === 'payment' ? '500px' : '0',
                    visibility: activeSection === 'payment' ? 'visible' : 'hidden',
                }}
            >
                {companyDetails?.id && (
                    <PayrocCreditCardPaymentForm
                        setCreditCardSessionTimer={setCreditCardSessionTimer}
                        tripSetting={tripSetting}
                        handleSubmit={handleSubmitPayment}
                        companyID={companyDetails.id}
                    />
                )}
            </div>
        </div>
    );

    const fiservPaymentForm = (
        <div className="my-3 w-100">
            <Row>
                <Col xs={12}>
                    <FormGroup className="name-card-box">
                        <Form.Label>Name on Card</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder="Card Name"
                            {...(companyDetails?.paymentProcessorTypeID === 3
                                ? register('name', { required: 'Please Enter a Name' })
                                : { ref: register })}
                            isInvalid={!!errors['name']}
                            className="payment-form-input"
                        />
                        <Form.Control.Feedback id="companyID" type="invalid">
                            {errors['name']?.message}
                        </Form.Control.Feedback>
                    </FormGroup>
                </Col>
                <CustomerInfoFormSection
                    states={states}
                    countries={countries}
                    errors={errors}
                    register={register}
                    includeName={false}
                />
                {fiservHostedFieldsActive ? (
                    <Col xs={12}>
                        <FormGroup className="mb-0">
                            <Form.Label className="text-dark">
                                Card Details<span className="text-danger">*</span>
                            </Form.Label>
                            <FiservCardNumberField
                                key={fiservFieldKey}
                                ref={fiservFieldRef}
                                onTokenReceived={setFiservToken}
                                onErrorReceived={setFiservError}
                                companyID={companyDetails.id}
                                panelColor="#ffffff"
                                textColor="#343a40"
                                showSecurelyCapturedMessage={!!fiservToken}
                                errorMessage={fiservError}
                            />
                        </FormGroup>
                    </Col>
                ) : (
                    isFiservHostedFieldsEnabled !== null && (
                        <>
                            <Col xs={12}>
                                <FormGroup>
                                    <Form.Label className="text-dark">
                                        Card Number<span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="cardNumber"
                                        name="cardNumber"
                                        required={true}
                                        maxLength={16}
                                        placeholder="Enter credit card number with no spaces or dashes"
                                        {...(companyDetails?.paymentProcessorTypeID === 3
                                            ? register('cardNumber', { required: 'Please Enter a Card Number' })
                                            : { ref: register })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={4}>
                                <FormGroup>
                                    <Form.Label className="text-dark">
                                        Card Type<span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="select"
                                        id="cardType"
                                        name="cardType"
                                        {...(companyDetails?.paymentProcessorTypeID === 3
                                            ? register('cardType', { required: 'Please Select a Card Type' })
                                            : { ref: register })}
                                        required={true}
                                    >
                                        <option value=""></option>
                                        <option value="American Express">American Express</option>
                                        <option value="Discover">Discover</option>
                                        <option value="Mastercard">Mastercard</option>
                                        <option value="Visa">Visa</option>
                                    </Form.Control>
                                </FormGroup>
                            </Col>
                            <Col xs={4}>
                                <FormGroup>
                                    <Form.Label className="text-dark">
                                        CVV<span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        id="cvv"
                                        name="cvv"
                                        required={true}
                                        placeholder="XXX"
                                        maxLength={3}
                                        {...(companyDetails?.paymentProcessorTypeID === 3
                                            ? register('cvv', { required: 'Please Enter a CVV' })
                                            : { ref: register })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={4}>
                                <FormGroup>
                                    <Form.Label className="text-dark">
                                        Card Expiration<span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="cardExpiration"
                                        name="cardExpiration"
                                        required={true}
                                        placeholder="MM/YY"
                                        maxLength={5}
                                        {...(companyDetails?.paymentProcessorTypeID === 3
                                            ? register('cardExpiration', {
                                                  required: 'Please Enter a Card Expiration',
                                              })
                                            : { ref: register })}
                                    />
                                </FormGroup>
                            </Col>
                        </>
                    )
                )}

                <div className="button-box d-flex justify-content-center">
                    <Button onClick={handleSubmitPayment}>
                        <span className="mx-2">
                            <i className="fas fa-lock"></i>
                        </span>
                        Accept & Pay
                    </Button>
                </div>
            </Row>
        </div>
    );

    const paymentForms = () => {
        return (
            <Row>
                <Col lg={8} md={7} xs={12} className="d-flex justify-content-center">
                    <div className="left-panel-card">
                        <div>
                            {creditCardSessionTimer > 0 && (
                                <div
                                    style={{
                                        padding: '0.5rem 1rem',
                                        marginBottom: '1rem',
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffeeba',
                                        borderRadius: '4px',
                                        color: '#856404',
                                        fontSize: '0.9rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <i className="fas fa-clock mr-2"></i>
                                    Session expires in: {Math.floor(creditCardSessionTimer / 60)}:
                                    {(creditCardSessionTimer % 60).toString().padStart(2, '0')}
                                </div>
                            )}
                            <form onSubmit={(e) => handleSubmitPayment(e)}>
                                <div>
                                    <h5 className="heading-first">
                                        Thank you for your business. Please use the following form to make your payment.
                                    </h5>
                                    <h3 className="invoice-title">{`Invoice Total : $${formatCurrency(
                                        invoiceTotal
                                    )}`}</h3>

                                    <FormGroup className="payment-card">
                                        <Form.Label className="label-payment">Payment Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="amount"
                                            id="amount"
                                            placeholder="Payment Amount"
                                            value={
                                                basePaymentAmount
                                                    ? basePaymentAmount
                                                    : totalPaymentAmount
                                                      ? totalPaymentAmount
                                                      : ''
                                            }
                                            onChange={(e) => {
                                                setInvoiceAmount(Number(e.target.value));
                                                setBasePaymentAmount(Number(e.target.value));

                                                if (e.target.value > invoiceTotal) {
                                                    setIsOverPayment(true);
                                                } else {
                                                    setIsOverPayment(false);
                                                }
                                                if (surcharge.percentage) {
                                                    setSurcharge({
                                                        ...surcharge,
                                                        amount: formatCurrency(
                                                            e.target.value * (surcharge.percentage / 100)
                                                        ),
                                                    });
                                                    setTotalPaymentAmount(
                                                        roundTo(e.target.value * (1 + surcharge.percentage / 100), 2)
                                                    );
                                                } else {
                                                    setTotalPaymentAmount(Number(e.target.value));
                                                }
                                            }}
                                            isInvalid={!totalPaymentAmount || isOverPayment}
                                        />
                                        <Form.Control.Feedback id="amount" type="invalid">
                                            {isOverPayment ? 'Overpayment' : 'Please enter a payment amount'}
                                        </Form.Control.Feedback>
                                    </FormGroup>

                                    {companyDetails?.paymentProcessorTypeID === 3
                                        ? fiservPaymentForm
                                        : payrocPaymentForm}
                                </div>

                                <div className="comment-paragraph">
                                    <p>
                                        {`By selecting accept and pay, I accept the terms of service and have read and acknowledge the privacy statement. I also allow ${
                                            companyDetails.displayName ? companyDetails.displayName : ''
                                        } to charge $${formatCurrency(
                                            (Number(invoiceAmount) || Number(amountOutstanding)) +
                                                Number(surcharge.amount)
                                        )} to my card on ${moment().format('ll')}`}
                                    </p>
                                </div>
                            </form>
                        </div>
                    </div>
                </Col>
                <Col lg={4} md={5} xs={12} className="d-flex flex-column justify-content-between">
                    <div className="right-panel-card">
                        <div className="right-panel-one">
                            <div className="logo-images mt-2">
                                {tripSetting && tripSetting.companyLogoUrl ? (
                                    <Image src={tripSetting.companyLogoUrl} className="m-0 p-0" />
                                ) : (
                                    ''
                                )}
                            </div>
                            <div className="sample-box">
                                <span className="title mt-3">{`${
                                    companyDetails.displayName ? companyDetails.displayName : ''
                                }`}</span>
                            </div>
                            <div>
                                <div className="d-flex align-items-center justify-content-between my-2 invoice-title">
                                    <span className="title">Invoice Number</span>
                                    <span className="number">{`${
                                        invoice.invoiceNumber ? invoice.invoiceNumber : ''
                                    }`}</span>
                                </div>
                                <div className="d-flex align-items-center justify-content-between my-2  invoice-title">
                                    <span className="title">Due Date</span>
                                    <span className="number">{`${
                                        invoice.invoiceDueDate ? moment(invoice.invoiceDueDate).format('ll') : ''
                                    }`}</span>
                                </div>
                                <div className="d-flex align-items-center justify-content-between my-2  invoice-title">
                                    <span className="title">Invoice Amount</span>
                                    <span className="number">{`${
                                        invoiceTotal ? `$${formatCurrency(invoiceTotal)}` : '$0.00'
                                    }`}</span>
                                </div>
                            </div>
                        </div>

                        <div className="right-panel-two ">
                            <div className="d-flex align-items-center justify-content-between invoice-title my-2">
                                <span className="title">Balance Remaining</span>
                                <span className="number">{`${
                                    displayAmountOutstanding ? `$${formatCurrency(displayAmountOutstanding)}` : '$0.00'
                                }`}</span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between invoice-title my-2">
                                <span className="title">Invoice Payment</span>
                                <span className="number">{`${
                                    invoiceAmount
                                        ? `$${formatCurrency(invoiceAmount)}`
                                        : amountOutstanding
                                          ? `$${formatCurrency(amountOutstanding)}`
                                          : '$0.00'
                                }`}</span>
                            </div>
                            {surcharge.percentage ? (
                                <div>
                                    <div className="d-flex align-items-center justify-content-between invoice-title my-2">
                                        <span className="title">CC Surcharge</span>
                                        <span className="number">{`$${surcharge.amount}`}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between invoice-title my-2">
                                        <span className="title">Total Payment</span>
                                        <span className="number">{`$${formatCurrency(totalPaymentAmount)}`}</span>
                                    </div>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>

                        <div className="right-panel-three">
                            <div className="button-view">
                                <Button onClick={handleViewInvoice}>View Invoice</Button>
                            </div>
                        </div>

                        <div className="my-4">
                            <span className="mr-2">
                                <i className="fas fa-lock"></i>
                            </span>{' '}
                            <span className="info-title">Information is protected and kept confidential</span>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    };

    return (
        <>
            <div style={{ backgroundColor: `${tripSetting.sectionColor}` }}>
                <div
                    className="d-flex justify-content-end  align-items-center mini-navbar"
                    style={{ backgroundColor: `${tripSetting.textColor}` }}
                >
                    <div className="d-flex  p-4">
                        <Image
                            src={
                                'https://storage.googleapis.com/the-bus-network-bucket/default/Powered-By-TBN-Logo.png'
                            }
                            className="m-0 p-0"
                        />
                    </div>
                </div>
                <div>
                    <div className="main-wrap-box">
                        <Container>
                            {loadError
                                ? loadErrorMessage
                                : !disablePayment
                                  ? Number(formState.stage) === 1
                                      ? confirmation()
                                      : paymentForms()
                                  : DisablePaymentModal()}
                        </Container>
                    </div>
                </div>
                {(loading || isFiservHostedFieldsEnabled === null) && <Loading loading={true} />}
            </div>
        </>
    );
};

export default InvoicePayment;
